import {
  type CareOrder,
  getShowJSONContextItem,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import type { ReactNode } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { useCareOrderActionMenuItems } from '../useCareOrderActionMenuItems';
import { useModifyNettingMenuItem } from '../useModifyNettingMenuItem';
import type { useNetCareOrdersMenuItem } from '../useNetCareOrdersMenuItem';

interface UseCareOrderManagementMenuParams {
  netCareOrdersMenu: ReturnType<typeof useNetCareOrdersMenuItem<CareOrder>>;
}

export function useCareOrderManagementMenu({ netCareOrdersMenu }: UseCareOrderManagementMenuParams): {
  getContextMenuItems: (params: GetContextMenuItemsParams<CareOrder>) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const modifyNettingMenu = useModifyNettingMenuItem<CareOrder>();
  const careOrderActionMenuItems = useCareOrderActionMenuItems();
  const { handleClickJson, jsonModal } = useJsonModal<CareOrder>();
  const { enableCareOrderNetting } = useFeatureFlag();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrder>) => {
    const entity = params.node?.data;
    const selectedCareOrders = getSelectedCareOrderRows(params);
    const showJsonItem = entity ? getShowJSONContextItem({ params, handleClickJson }) : null;
    const modifyItem = entity ? careOrderActionMenuItems.getModifyMenuItem(entity) : null;
    const cancelItem = entity
      ? careOrderActionMenuItems.getCancelMenuItem({
          entity,
          isCancelable: entity.isCancelable,
        })
      : null;
    const forceCancelItem = entity ? careOrderActionMenuItems.getForceCancelMenuItem(entity) : null;
    const defaultContextMenuItems = getDefaultContextMenuItems(params);

    const netCareOrdersItem = enableCareOrderNetting
      ? netCareOrdersMenu.getMenuItem({
          selectedCareOrders,
        })
      : null;
    const modifyNettingItem = enableCareOrderNetting
      ? modifyNettingMenu.getMenuItem({
          contextCareOrder: entity,
          selectedCareOrders,
        })
      : null;

    return compact([
      modifyNettingItem,
      netCareOrdersItem,
      'separator',
      modifyItem,
      cancelItem,
      forceCancelItem,
      'separator',
      showJsonItem,
      'separator',
      ...defaultContextMenuItems,
    ]);
  });

  return {
    getContextMenuItems,
    dialogs: (
      <>
        {jsonModal}
        {netCareOrdersMenu.dialogs}
        {modifyNettingMenu.dialogs}
      </>
    ),
  };
}

function getSelectedCareOrderRows(params: GetContextMenuItemsParams<CareOrder>): CareOrder[] {
  return getSelectedCareOrderRowsFromNodes(params.api.getSelectedNodes());
}

export function getSelectedCareOrderRowsFromNodes(nodes: IRowNode<CareOrder>[]): CareOrder[] {
  const isCareOrderRowNode = (node: IRowNode<CareOrder>): node is IRowNode<CareOrder> & { data: CareOrder } =>
    node.data != null && !node.data.isNetted;

  return nodes.filter(isCareOrderRowNode).map(node => node.data);
}
